import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { withI18next } from 'lib/i18n/withI18next'
import useIntersection from 'components/custom-hooks/useIntersection'
import getVideoPosterUrl from './poster'

const ImageCarouselAutoPlayVideo = ({
  src,
  loop,
  poster,
  autoPlay,
  className,
  onVideoEnd,
  customSegmentTracking,
  loadWhenActive,
  desktopHeroPoster,
}) => {
  const videoRef = useRef(null)
  const isIntersecting = useIntersection(videoRef, {
    once: false,
  })
  // Homepage slides also need to be active: slides can intersect during
  // Swiper setup/transitions (and old browsers may lack IntersectionObserver).
  // Retain an activated source on return visits without admitting a new URL
  // while that slide is inactive. Other callers keep intersection-only loading.
  const [loadedSrc, setLoadedSrc] = useState(null)
  const hasLoadedSource = loadWhenActive
    ? loadedSrc === src
    : loadedSrc !== null
  const isPlaybackReady = !loadWhenActive || hasLoadedSource
  const compressedPoster = useMemo(
    () => getVideoPosterUrl(poster, desktopHeroPoster),
    [poster, desktopHeroPoster],
  )

  useEffect(() => {
    if (isIntersecting && (!loadWhenActive || autoPlay)) {
      setLoadedSrc(src)
    }
  }, [isIntersecting, loadWhenActive, autoPlay, src])

  useEffect(() => {
    if (videoRef?.current && isPlaybackReady) {
      if (autoPlay && isIntersecting) {
        const playRequest = videoRef.current.play()
        if (loadWhenActive && playRequest) {
          // A rapid swipe or browser autoplay policy may reject play().
          // Keep the poster and allow playback on the next activation.
          playRequest.catch(() => {})
        }
        if (customSegmentTracking) {
          customSegmentTracking()
        }
      } else {
        videoRef?.current?.pause()
        videoRef.current.currentTime = 0
      }
    }
  }, [
    isIntersecting,
    autoPlay,
    videoRef,
    customSegmentTracking,
    isPlaybackReady,
    loadWhenActive,
  ])

  const handleVideoEnd = () => {
    if (onVideoEnd) {
      onVideoEnd()
    }
  }

  return (
    <video
      ref={videoRef}
      className={className}
      loop={loop}
      muted
      {...(hasLoadedSource && { src })}
      {...(loadWhenActive && { preload: 'none' })}
      playsInline
      poster={compressedPoster}
      type="video/mp4"
      controls={false}
      onEnded={handleVideoEnd}
    />
  )
}

ImageCarouselAutoPlayVideo.defaultProps = {
  src: '',
  poster: '',
  loop: true,
  autoPlay: true,
  className: null,
  onVideoEnd: null,
  customSegmentTracking: null,
  loadWhenActive: false,
  desktopHeroPoster: false,
}

ImageCarouselAutoPlayVideo.propTypes = {
  loop: PropTypes.bool,
  src: PropTypes.string,
  poster: PropTypes.string,
  autoPlay: PropTypes.bool,
  onVideoEnd: PropTypes.func,
  className: PropTypes.string,
  customSegmentTracking: PropTypes.func,
  loadWhenActive: PropTypes.bool,
  desktopHeroPoster: PropTypes.bool,
}

export default withI18next()(ImageCarouselAutoPlayVideo)
